import { usePrevSelectedCategoryRef } from '@/features/pos/components/ProductCatalog/parts/CategoryBar/hooks/usePrevSelectedCategoryRef'
import { useScrollToCategory } from '@/features/pos/components/ProductCatalog/parts/CategoryBar/hooks/useScrollToCategory'
import {
    CategoryRegistry,
    MeasureItemLeftPosition,
    MeasureSubcategoriesContainerWidth,
    ScrollMeasureHandlers,
    SelectedCategory,
    WrapContainerRefs,
} from '@/features/pos/components/ProductCatalog/parts/CategoryBar/types'
import { getNextSelectedCategory } from '@/features/pos/components/ProductCatalog/parts/CategoryBar/utils/getNextSelectedCategory'
import { getSelectedCategoryWithMetadata } from '@/features/pos/components/ProductCatalog/parts/CategoryBar/utils/getSelectedCategoryWithMetadata'
import { RefObject, useCallback, useRef } from 'react'

export const useCategorySelectionHandlers = (
    { id, subcategoryId }: SelectedCategory,
    registryRef: RefObject<CategoryRegistry>,
    wrapContainerRefs: WrapContainerRefs,
    onPress: (id?: string, subcategoryId?: string) => void,
    disableScroll?: boolean
) => {
    const prevSelectedRef = usePrevSelectedCategoryRef(id, subcategoryId)

    const lastSelectedMeasureSubcategoriesContainerWidthRef =
        useRef<MeasureSubcategoriesContainerWidth>(undefined)

    const { scrollToCategory, cancelPendingScroll } = useScrollToCategory(
        wrapContainerRefs,
        disableScroll
    )

    const getCategoryForScroll = useCallback(
        (category?: SelectedCategory) => {
            if (!category?.id) return undefined

            const metadata = registryRef.current[category.id]

            return getSelectedCategoryWithMetadata(category, metadata)
        },
        [registryRef]
    )

    const handleCategoryWithSubcategoriesPress = useCallback(
        (
            id: string,
            subcategoryId: string | undefined,
            { measureItemLeftPosition, measureSubcategoriesContainerWidth }: ScrollMeasureHandlers
        ) => {
            const prev = getCategoryForScroll(prevSelectedRef.current)
            const pressed = getCategoryForScroll({ id, subcategoryId })!

            void scrollToCategory(
                { prev, pressed },
                {
                    measureItemLeftPosition,
                    lastSelectedMeasureSubcategoriesContainerWidth:
                        lastSelectedMeasureSubcategoriesContainerWidthRef.current,
                }
            )

            lastSelectedMeasureSubcategoriesContainerWidthRef.current =
                measureSubcategoriesContainerWidth

            const category = getNextSelectedCategory({ id, subcategoryId }, prevSelectedRef.current)

            prevSelectedRef.current = category
            onPress(category.id, category.subcategoryId)
        },
        [onPress, prevSelectedRef, scrollToCategory, getCategoryForScroll]
    )

    const handleCategoryPress = useCallback(
        (id: string, measureItemLeftPosition: MeasureItemLeftPosition) => {
            const prev = getCategoryForScroll(prevSelectedRef.current)
            const pressed = getCategoryForScroll({ id })!

            void scrollToCategory(
                { prev, pressed },
                {
                    measureItemLeftPosition,
                    lastSelectedMeasureSubcategoriesContainerWidth:
                        lastSelectedMeasureSubcategoriesContainerWidthRef.current,
                }
            )

            const category = getNextSelectedCategory({ id }, prevSelectedRef.current)

            lastSelectedMeasureSubcategoriesContainerWidthRef.current = undefined
            prevSelectedRef.current = category
            onPress(category.id)
        },
        [onPress, prevSelectedRef, scrollToCategory, getCategoryForScroll]
    )

    return {
        handleCategoryWithSubcategoriesPress,
        handleCategoryPress,
        cancelPendingScroll,
    } as const
}
